import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  CalendarBlank,
  MapPin,
  Users,
  ForkKnife,
  Storefront,
  CheckCircle,
  CircleNotch,
  Clock,
  XCircle,
  ChatCircleText,
  Sparkle,
  ArrowsClockwise,
  ShareNetwork,
  Plus,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * InquiryStatus — Phase 3 host self-service status page.
 *
 * Reached via /inquiry/:id?token=... in the host confirmation email +
 * the "View my inquiry →" link on the FindATruck confirmation step.
 *
 * Premium UX (this is THE conversion-loop touchpoint that brings
 * inquiry-recipients back to the platform — needs to feel polished
 * enough to share):
 *   - Hero stat: "3 of 5 trucks have replied"
 *   - Per-truck cards w/ logo + status pill + reply preview
 *   - Live refresh CTA (data refetches without a page reload)
 *   - Shareable URL: hosts often forward this to a co-organizer
 */

const STATUS_META = {
  replied: {
    label: "Replied",
    Icon: CheckCircle,
    iconClass: "text-emerald-600",
    pillClass: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  },
  declined: {
    label: "Declined",
    Icon: XCircle,
    iconClass: "text-rose-500",
    pillClass: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  },
  delivered: {
    label: "Awaiting reply",
    Icon: Clock,
    iconClass: "text-amber-500",
    pillClass: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  },
  viewed: {
    label: "Seen, no reply yet",
    Icon: Clock,
    iconClass: "text-amber-500",
    pillClass: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  },
  // Feb 2026 SOP — truck went silent past the 48h window. Hard-blocked
  // from replying via TruckTrackr. UX-friendly framing for the host
  // ("Window closed") makes it clear there's nothing more to wait on.
  expired_silent: {
    label: "Window closed",
    Icon: XCircle,
    iconClass: "text-muted-foreground",
    pillClass: "bg-muted text-muted-foreground ring-1 ring-border",
  },
  expired: {
    label: "No reply",
    Icon: XCircle,
    iconClass: "text-muted-foreground",
    pillClass: "bg-muted text-muted-foreground ring-1 ring-border",
  },
};

function statusKeyForRecipient(r) {
  // Backend may set response_kind="decline" but status="replied" —
  // surface that as "declined" for the host's eye.
  if (r.response_kind === "decline") return "declined";
  return r.status || "delivered";
}

function formatEventDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}

function StatusPill({ statusKey }) {
  const meta = STATUS_META[statusKey] || STATUS_META.delivered;
  const Icon = meta.Icon;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold " +
        meta.pillClass
      }
      data-testid={`recipient-status-${statusKey}`}
    >
      <Icon weight="fill" className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function RecipientCard({ recipient }) {
  const statusKey = statusKeyForRecipient(recipient);
  const isReplied = statusKey === "replied";
  const isDeclined = statusKey === "declined";
  const profileHref = recipient.truck_slug ? `/t/${recipient.truck_slug}` : null;

  return (
    <li
      className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`inquiry-recipient-${recipient.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl overflow-hidden bg-muted grid place-items-center flex-none ring-1 ring-border">
          {recipient.truck_logo_url ? (
            <img
              src={recipient.truck_logo_url}
              alt={recipient.truck_name}
              className="h-full w-full object-cover"
            />
          ) : (
            <Storefront weight="duotone" className="h-7 w-7 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="font-semibold text-base sm:text-lg leading-tight truncate">
              {profileHref ? (
                <Link to={profileHref} className="hover:underline">
                  {recipient.truck_name}
                </Link>
              ) : (
                recipient.truck_name
              )}
            </h3>
            <StatusPill statusKey={statusKey} />
          </div>
          {recipient.truck_blurb && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {recipient.truck_blurb}
            </p>
          )}
          {recipient.matched_cuisines && recipient.matched_cuisines.length > 0 && (
            <div
              className="mt-2 flex flex-wrap items-center gap-1.5"
              data-testid={`recipient-matched-cuisines-${recipient.id}`}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Matched on
              </span>
              {recipient.matched_cuisines.slice(0, 5).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full ring-1 ring-primary/20"
                >
                  <ForkKnife weight="fill" className="h-2.5 w-2.5" />
                  {c}
                </span>
              ))}
            </div>
          )}
          {(isReplied || isDeclined) && recipient.response_summary && (
            <div
              className={
                "mt-3 rounded-lg p-3 text-sm border " +
                (isDeclined
                  ? "border-rose-100 bg-rose-50/50 text-rose-900"
                  : "border-emerald-100 bg-emerald-50/40 text-emerald-900")
              }
              data-testid={`recipient-response-summary-${recipient.id}`}
            >
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-1 opacity-70">
                <ChatCircleText weight="fill" className="h-3 w-3" />
                Preview
              </div>
              <p className="leading-relaxed line-clamp-3 whitespace-pre-wrap">
                {recipient.response_summary}
              </p>
              <p className="text-[11px] opacity-60 mt-1.5">
                Full reply sent to your email · {timeAgo(recipient.responded_at)}
              </p>
            </div>
          )}
          {!isReplied && !isDeclined && (statusKey === "expired" || statusKey === "expired_silent") && (
            <p className="mt-2 text-xs text-muted-foreground italic">
              {statusKey === "expired_silent"
                ? "This truck didn't reply within 48 hours — the window has closed."
                : "This truck did not reply within the window. Their email is still on file."}
            </p>
          )}
          {profileHref && (
            <div className="mt-2">
              <Link
                to={profileHref}
                className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
                data-testid={`recipient-view-profile-${recipient.id}`}
              >
                View profile →
              </Link>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default function InquiryStatus() {
  const { inquiryId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // Expand modal state (Feb 2026 SOP — "Reach more trucks" flow)
  const [expandOpen, setExpandOpen] = useState(false);
  const expandAutoOpened = useRef(false);

  const fetchStatus = async ({ silent = false } = {}) => {
    if (!token) {
      setError("This link is missing its access token. Check the email we sent you.");
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.get(`/event-inquiries/${inquiryId}?token=${encodeURIComponent(token)}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      const status = e?.response?.status;
      setError(
        status === 404
          ? "This inquiry link is invalid or has been removed."
          : formatApiError(e) || "We couldn't load this inquiry. Please try again."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [inquiryId, token]);

  // Auto-open the expand modal once when the host arrives via the 48h
  // email link (URL contains ?expand=1). Only fires once per page-load
  // so refresh doesn't re-pop the modal if the user closed it.
  useEffect(() => {
    if (
      !expandAutoOpened.current &&
      data &&
      params.get("expand") === "1" &&
      !expandOpen
    ) {
      expandAutoOpened.current = true;
      setExpandOpen(true);
    }
  }, [data, params, expandOpen]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My TruckTrackr inquiry", url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      // user cancelled — silent
    }
  };

  const orderedRecipients = useMemo(() => {
    if (!data?.recipients) return [];
    // Replies first, then declined, then pending, then expired — host's
    // eye should land on the good news first.
    const rank = { replied: 0, declined: 1, viewed: 2, delivered: 2, expired: 3, expired_silent: 3 };
    return [...data.recipients].sort((a, b) => {
      const ka = statusKeyForRecipient(a);
      const kb = statusKeyForRecipient(b);
      return (rank[ka] ?? 9) - (rank[kb] ?? 9);
    });
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center px-4 bg-gradient-to-br from-primary/5 via-background to-background">
        <div
          className="text-center text-muted-foreground"
          data-testid="inquiry-status-loading"
        >
          <CircleNotch className="h-8 w-8 mx-auto animate-spin text-primary" />
          <p className="mt-3 text-sm">Loading your inquiry…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-4 bg-background">
        <div
          className="text-center max-w-md"
          data-testid="inquiry-status-error"
        >
          <XCircle weight="duotone" className="h-12 w-12 mx-auto text-rose-400" />
          <h1 className="mt-4 text-xl font-semibold">Inquiry not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Link to="/find-a-truck">
            <Button className="mt-6" data-testid="inquiry-status-new-inquiry-btn">
              Start a new inquiry
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const inq = data.inquiry || {};
  const counts = data.counts || { total: 0, replied: 0, declined: 0, pending: 0, expired: 0 };
  const responseRate =
    counts.total > 0
      ? Math.round(((counts.replied + counts.declined) / counts.total) * 100)
      : 0;
  const isClosed = inq.status === "closed";

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background pb-20">
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14" data-testid="inquiry-status-hero">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary mb-3">
          <Sparkle weight="fill" className="h-3 w-3" />
          Your event inquiry
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          {counts.replied > 0 ? (
            <>
              <span className="text-primary">{counts.replied}</span>{" "}
              {counts.replied === 1 ? "truck has" : "trucks have"} replied
            </>
          ) : counts.declined > 0 && counts.pending === 0 ? (
            <>Still searching — check the alternates below</>
          ) : (
            <>Waiting on {counts.pending} {counts.pending === 1 ? "truck" : "trucks"}…</>
          )}
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-xl">
          We sent your inquiry to {counts.total} food{" "}
          {counts.total === 1 ? "truck" : "trucks"}. Replies land in{" "}
          <span className="font-medium text-foreground">{inq.customer_email}</span> —
          this page mirrors what&apos;s happened so you can scan the bids fast.
        </p>

        {/* Quick stats strip */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="inquiry-status-counts">
          <StatTile label="Replied" value={counts.replied} tone="emerald" />
          <StatTile label="Declined" value={counts.declined} tone="rose" />
          <StatTile label="Pending" value={counts.pending} tone="amber" />
          <StatTile label="Response rate" value={`${responseRate}%`} tone="neutral" />
        </div>

        {/* Inquiry summary card */}
        <div
          className="mt-8 rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-sm"
          data-testid="inquiry-status-summary"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <SummaryRow Icon={CalendarBlank} label="Event date" value={formatEventDate(inq.event_date_start)} />
            <SummaryRow Icon={MapPin} label="Location" value={inq.event_location_formatted || inq.event_zip} />
            <SummaryRow Icon={Users} label="Party size" value={`${inq.party_size} guests`} />
            <SummaryRow
              Icon={ForkKnife}
              label="Cuisine"
              value={
                (inq.cuisine_preferences || []).length > 0
                  ? inq.cuisine_preferences.join(", ")
                  : "Any cuisine — flexible"
              }
            />
          </div>
          {inq.message && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Your message
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
                {inq.message}
              </p>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchStatus({ silent: true })}
            disabled={refreshing}
            data-testid="inquiry-status-refresh-btn"
          >
            <ArrowsClockwise
              weight="bold"
              className={"h-3.5 w-3.5 mr-1.5 " + (refreshing ? "animate-spin" : "")}
            />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            data-testid="inquiry-status-share-btn"
          >
            <ShareNetwork weight="bold" className="h-3.5 w-3.5 mr-1.5" />
            Share with co-host
          </Button>
          {!isClosed && (
            <Button
              size="sm"
              onClick={() => setExpandOpen(true)}
              data-testid="inquiry-status-expand-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus weight="bold" className="h-3.5 w-3.5 mr-1.5" />
              Reach more trucks
            </Button>
          )}
        </div>

        {isClosed && (
          <div
            className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="inquiry-status-closed-notice"
          >
            This inquiry was closed by TruckTrackr support. If this was unexpected,
            reply to the confirmation email and we&apos;ll look into it.
          </div>
        )}
      </section>

      {/* Recipients list */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-10">
        <h2 className="text-lg font-semibold mb-4">Trucks we contacted</h2>
        {orderedRecipients.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="inquiry-status-no-recipients">
            We weren&apos;t able to reach any trucks — please reply to the confirmation
            email and we&apos;ll help by hand.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="inquiry-status-recipient-list">
            {orderedRecipients.map((r) => (
              <RecipientCard key={r.id} recipient={r} />
            ))}
          </ul>
        )}
      </section>

      {/* Footer CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-12">
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Need to reach out to more trucks?
          </p>
          <Link to="/find-a-truck">
            <Button className="mt-3" data-testid="inquiry-status-new-inquiry-cta">
              Start a new inquiry
            </Button>
          </Link>
        </div>
      </section>

      {/* Expand modal — "Reach more trucks" */}
      {expandOpen && (
        <ExpandModal
          inquiryId={inquiryId}
          token={token}
          onClose={() => setExpandOpen(false)}
          onSent={() => {
            setExpandOpen(false);
            fetchStatus({ silent: true });
          }}
        />
      )}
    </div>
  );
}

function ExpandModal({ inquiryId, token, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/event-inquiries/${inquiryId}/expand-candidates?token=${encodeURIComponent(token)}`
        );
        if (cancel) return;
        setCandidates(r.data?.candidates || []);
        setMeta({
          lifetime_remaining: r.data?.lifetime_remaining ?? 0,
          per_call_cap: r.data?.per_call_cap ?? 10,
          cooldown_until: r.data?.cooldown_until || null,
        });
      } catch (e) {
        if (!cancel) setErrMsg(formatApiError(e) || "Couldn't load more trucks. Try again later.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [inquiryId, token]);

  const maxSelectable = Math.min(meta.per_call_cap || 10, meta.lifetime_remaining || 0);
  const cooldownActive = !!meta.cooldown_until;
  const cooldownLabel = cooldownActive ? new Date(meta.cooldown_until).toLocaleString() : "";

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < maxSelectable) next.add(id);
      else toast.error(`You can pick up to ${maxSelectable} more trucks.`);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one truck.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/event-inquiries/${inquiryId}/expand`, {
        token,
        selected_truck_ids: Array.from(selected),
      });
      toast.success(`Sent to ${r.data?.sent_count || selected.size} more trucks`);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't send. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="inquiry-expand-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-card shadow-xl border border-border flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold leading-tight">Reach more trucks</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {cooldownActive
                ? `You recently expanded — next expand available after ${cooldownLabel}.`
                : meta.lifetime_remaining > 0
                  ? `Pick up to ${maxSelectable} of these nearby trucks. They haven't been contacted on this inquiry yet.`
                  : "You've reached the cap for this inquiry."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid="inquiry-expand-close"
            aria-label="Close"
          >
            <X weight="bold" className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <CircleNotch className="h-6 w-6 mx-auto animate-spin text-primary" />
              <p className="mt-2 text-sm">Loading candidates…</p>
            </div>
          ) : errMsg ? (
            <div className="text-sm text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-lg p-3" data-testid="inquiry-expand-error">
              {errMsg}
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12" data-testid="inquiry-expand-empty">
              <Storefront weight="duotone" className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                No more nearby trucks match this inquiry right now. Check back as new trucks publish their profiles.
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="inquiry-expand-candidate-list">
              {candidates.map((c) => {
                const checked = selected.has(c.truck_id);
                return (
                  <li key={c.truck_id}>
                    <label
                      className={
                        "flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all " +
                        (checked
                          ? "border-primary bg-primary/5"
                          : "border-border bg-card hover:border-primary/40")
                      }
                      data-testid={`inquiry-expand-candidate-${c.truck_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(c.truck_id)}
                        className="mt-1 h-4 w-4 accent-primary flex-none"
                      />
                      <div className="h-10 w-10 rounded-lg overflow-hidden bg-muted grid place-items-center flex-none">
                        {c.logo_url ? (
                          <img src={c.logo_url} alt={c.truck_name} className="h-full w-full object-cover" />
                        ) : (
                          <Storefront weight="duotone" className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">{c.truck_name}</span>
                          {c.is_pro && (
                            <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded uppercase tracking-wide">
                              Pro
                            </span>
                          )}
                          {c.distance_miles != null && (
                            <span className="text-[11px] text-muted-foreground">
                              {c.distance_miles}mi
                            </span>
                          )}
                        </div>
                        {c.matched_cuisines && c.matched_cuisines.length > 0 && (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-primary/80">
                              Matched
                            </span>
                            {c.matched_cuisines.slice(0, 4).map((m) => (
                              <span key={m} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full ring-1 ring-primary/20 font-medium">
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {selected.size}/{maxSelectable} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              data-testid="inquiry-expand-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={submitting || selected.size === 0 || cooldownActive || maxSelectable === 0}
              data-testid="inquiry-expand-submit"
            >
              {submitting ? (
                <>
                  <CircleNotch className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                `Send to ${selected.size || 0} ${selected.size === 1 ? "truck" : "trucks"}`
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-900 ring-emerald-100",
    rose: "bg-rose-50 text-rose-900 ring-rose-100",
    amber: "bg-amber-50 text-amber-900 ring-amber-100",
    neutral: "bg-muted text-foreground ring-border",
  };
  return (
    <div
      className={
        "rounded-xl px-3 py-3 ring-1 text-center " + (tones[tone] || tones.neutral)
      }
    >
      <div className="text-xl sm:text-2xl font-bold leading-none">{value}</div>
      <div className="text-[11px] sm:text-xs mt-1 opacity-75 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}

function SummaryRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon weight="duotone" className="h-4 w-4 text-primary mt-0.5 flex-none" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-sm font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}
